import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Users, FileText, Calendar, Activity, TrendingUp, Heart, ShoppingCart, PawPrint } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';
import StatCard from './ui/StatCard';
import SectionCard from './ui/SectionCard';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';

const OverviewTab = ({ filters, onViewDetailed }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadOverviewStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getOverviewStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load overview stats:', err);
            setError(err.message || 'Failed to load statistics');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadOverviewStats();
    }, [loadOverviewStats]);

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={loadOverviewStats} className="retry-button">Retry</button>
            </div>
        );
    }

    const growthData = stats?.graph_data?.visitor_growth || [];
    const deviceData = stats?.graph_data?.device_stats || [];

    return (
        <div className="sd-container">
            <div className="sd-detailed-link-row">
                <button
                    onClick={onViewDetailed}
                    className="sd-detailed-link"
                >
                    Detailed <ArrowRight size={14} />
                </button>
            </div>

            {/* Primary KPI Cards */}
            <div className="sd-grid sd-grid--kpi-primary">
                <StatCard
                    icon={Users}
                    label="Total Users"
                    value={stats?.totalUsers || 0}
                    trend={stats?.trends?.userGrowth}
                    trendLabel="vs last period"
                    color="bg-blue-500"
                    loading={loading}
                />
                <StatCard
                    icon={FileText}
                    label="Total Posts"
                    value={stats?.totalPosts || 0}
                    trend={stats?.trends?.postEngagement}
                    trendLabel="engagement"
                    color="bg-violet-500"
                    loading={loading}
                />
                <StatCard
                    icon={Calendar}
                    label="Appointments"
                    value={stats?.totalAppointments || 0}
                    trend={stats?.trends?.appointmentRate}
                    trendLabel="completion"
                    color="bg-rose-500"
                    loading={loading}
                />
                <StatCard
                    icon={Activity}
                    label="Active Clinics"
                    value={stats?.activeClinics || 0}
                    color="bg-teal-500"
                    loading={loading}
                />
            </div>

            {/* Secondary KPI Cards */}
            <div className="sd-grid sd-grid--kpi-secondary">
                <StatCard
                    icon={PawPrint}
                    label="Total Pets"
                    value={stats?.totalPets || 0}
                    color="bg-orange-500"
                    loading={loading}
                />
                <StatCard
                    icon={Heart}
                    label="Adoption Posts"
                    value={stats?.adoptionPosts || 0}
                    color="bg-pink-500"
                    loading={loading}
                />
                <StatCard
                    icon={ShoppingCart}
                    label="Marketplace Orders"
                    value={stats?.marketplaceOrders || 0}
                    color="bg-emerald-500"
                    loading={loading}
                />
            </div>

            {/* Charts */}
            {!loading && (
                <div className="sd-grid sd-grid--charts sd-grid--last">
                    <SectionCard title="Visitor Growth" icon={TrendingUp} className="sd-card--wide">
                        <CustomAreaChart data={growthData} color="#8b5cf6" title="" />
                    </SectionCard>
                    <SectionCard title="Device Usage" icon={Activity}>
                        <CustomDonutChart
                            data={deviceData}
                            totalValue={deviceData.reduce((a, b) => a + b.value, 0)}
                            title=""
                        />
                    </SectionCard>
                </div>
            )}
        </div>
    );
};

export default OverviewTab;
